<!-- Payment Status Pages -->
<div class="container-fluid flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-50 to-gray-100 p-4">

  <!-- Success Page -->
  @if(data()?.session?.payment_status === 'paid' && data()?.session?.status === 'complete') {
  <mat-card class="max-w-md w-full text-center overflow-hidden shadow-2xl">
    <!-- Animated Background -->
    <div class="relative bg-gradient-to-r from-green-400 to-emerald-500 p-8">
      <div class="absolute inset-0 bg-white opacity-10"></div>

      <!-- Success Animation Container -->
      <div class="relative z-10">
        <!-- Animated Checkmark -->
        <div class="mx-auto w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4">
          <mat-icon class="text-4xl text-green-500">check_circle</mat-icon>
        </div>

        <h1 class="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
        <p class="text-green-100">Your transaction has been completed successfully</p>
      </div>

      <!-- Floating Particles Effect -->
      <div class="absolute top-4 left-4 w-2 h-2 bg-white rounded-full opacity-60 animate-ping"></div>
      <div class="absolute top-8 right-6 w-1 h-1 bg-white rounded-full opacity-40 animate-ping"
        style="animation-delay: 0.5s;"></div>
      <div class="absolute bottom-6 left-8 w-1.5 h-1.5 bg-white rounded-full opacity-50 animate-ping"
        style="animation-delay: 1s;"></div>
    </div>

    <mat-card-content class="p-6 mt-2 min-h-[384px]">
      <!-- Transaction Details -->
      <div class="rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between mb-3">
          <span class="text-gray-600 text-sm">Payment Intent ID</span>
          <span class="font-mono text-sm text-gray-800">{{ data()?.session?.payment_intent || data()?.session?.id
            }}</span>
        </div>
        <div class="flex items-center justify-between mb-3">
          <span class="text-gray-600 text-sm">Amount Paid</span>
          <span class="font-semibold text-lg text-gray-800">
            {{ (data()?.session?.amount_total / 100) | number:'1.2-2' }} {{ data()?.session?.currency?.toUpperCase() }}
          </span>
        </div>
        <div class="flex items-center justify-between mb-3">
          <span class="text-gray-600 text-sm">Credits Added</span>
          <span class="font-semibold text-lg text-emerald-600">
            {{ calculateCredits(data()?.session?.amount_total) }} Credits
          </span>
        </div>
        <div class="flex items-center justify-between mb-3">
          <span class="text-gray-600 text-sm">Customer</span>
          <span class="text-sm text-gray-800">{{ data()?.session?.customer_details?.name ||
            data()?.session?.customer_details?.email }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-gray-600 text-sm">Date & Time</span>
          <span class="text-sm text-gray-800">{{ (data()?.session?.created * 1000) | date: 'dd MMM yyyy hh:mm:a' }}</span>
        </div>
      </div>

      <!-- Success Features -->
      <div class="space-y-3 mb-6">
        <div class="flex items-center gap-3 text-left">
          <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <mat-icon class="text-green-600 text-sm">email</mat-icon>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-800">Receipt Sent</p>
            <p class="text-xs text-gray-500">Check {{ data()?.session?.customer_details?.email }} for the receipt</p>
          </div>
        </div>

        <div class="flex items-center gap-3 text-left">
          <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <mat-icon class="text-blue-600 text-sm">account_balance_wallet</mat-icon>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-800">Credits Updated</p>
            <p class="text-xs text-gray-500">Your account balance has been updated</p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="space-y-3">
        <button mat-stroked-button class="w-full py-2" (click)="downloadReceipt()">
          <mat-icon class="mr-2">download</mat-icon>
          Download Receipt
        </button>
      </div>
    </mat-card-content>
  </mat-card>
  }

  <!-- Failure Page -->
  @if(data()?.session?.payment_status === 'unpaid' || data()?.session?.status === 'open' || data()?.session?.status ===
  'expired') {
  <mat-card class="max-w-md w-full text-center overflow-hidden shadow-2xl">
    <!-- Error Background -->
    <div class="relative bg-gradient-to-r from-red-400 to-pink-500 p-8">
      <div class="absolute inset-0 bg-white opacity-10"></div>

      <!-- Error Animation Container -->
      <div class="relative z-10">
        <!-- Animated Error Icon -->
        <div class="mx-auto w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 animate-pulse">
          <mat-icon class="text-4xl text-red-500">error</mat-icon>
        </div>

        <h1 class="text-2xl font-bold text-white mb-2">
          {{ data()?.session?.status === 'expired' ? 'Payment Expired' : 'Payment Failed' }}
        </h1>
        <p class="text-red-100">
          {{ data()?.session?.status === 'expired' ? 'Your payment session has expired' : 'We couldn\'t process your
          payment' }}
        </p>
      </div>

      <!-- Error Particles -->
      <div class="absolute top-6 right-4 w-1 h-1 bg-white rounded-full opacity-60 animate-ping"></div>
      <div class="absolute bottom-8 left-6 w-1.5 h-1.5 bg-white rounded-full opacity-40 animate-ping"
        style="animation-delay: 0.8s;"></div>
    </div>

    <mat-card-content class="p-6 mt-2 min-h-[384px]">
      <!-- Error Details -->
      <div class="bg-red-50 border border-red-200 rounded-lg py-4 mb-6">
        <div class="flex items-start gap-3">
          <mat-icon class="text-red-500 mt-0.5">info</mat-icon>
          <div class="text-left">
            <h3 class="font-medium text-red-800 mb-1">Payment Error</h3>
            <p class="text-sm text-red-600 mb-2 text-wrap">
              @if(data()?.session?.status === 'expired') {
              Your payment session has expired. Please try again.
              } @else {
              Your payment could not be processed. Please check your payment details and try again.
              }
            </p>
            <p class="text-xs text-red-500">
              Session ID: {{ data()?.session?.id }}
            </p>
            @if(data()?.session?.amount_total) {
            <p class="text-xs text-red-500 mt-1">
              Amount: {{ (data()?.session?.amount_total / 100) | number:'1.2-2' }} {{
              data()?.session?.currency?.toUpperCase() }}
            </p>
            }
          </div>
        </div>
      </div>

      <!-- Common Issues -->
      <div class="text-left mb-6">
        <h3 class="font-medium text-gray-800 mb-3">Common Issues:</h3>
        <div class="space-y-2">
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <mat-icon class="text-gray-400 text-base">fiber_manual_record</mat-icon>
            <span>Insufficient account balance</span>
          </div>
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <mat-icon class="text-gray-400 text-base">fiber_manual_record</mat-icon>
            <span>Card expired or blocked</span>
          </div>
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <mat-icon class="text-gray-400 text-base">fiber_manual_record</mat-icon>
            <span>Network connection issues</span>
          </div>
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <mat-icon class="text-gray-400 text-base">fiber_manual_record</mat-icon>
            <span>Bank security restrictions</span>
          </div>
          @if(data()?.session?.status === 'expired') {
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <mat-icon class="text-gray-400 text-base">fiber_manual_record</mat-icon>
            <span>Payment session timeout</span>
          </div>
          }
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="space-y-3">
        @if(data()?.session?.url && data()?.session?.status === 'open') {
        <button mat-raised-button color="primary" class="w-full py-3" (click)="retryPayment()">
          <mat-icon class="mr-2">refresh</mat-icon>
          Try Again
        </button>
        }
      </div>
    </mat-card-content>
  </mat-card>
  }

  <!-- Loading/Pending State -->
  @if(!data()?.session || (data()?.session?.payment_status !== 'paid' && data()?.session?.payment_status !== 'unpaid' &&
  data()?.session?.status !== 'complete' && data()?.session?.status !== 'open' && data()?.session?.status !==
  'expired')) {
  <mat-card class="max-w-md w-full text-center overflow-hidden shadow-2xl">
    <div class="relative bg-gradient-to-r from-blue-400 to-indigo-500 p-8">
      <div class="absolute inset-0 bg-white opacity-10"></div>

      <div class="relative z-10">
        <div class="mx-auto w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4">
          <mat-icon class="text-4xl text-blue-500 animate-spin">refresh</mat-icon>
        </div>

        <h1 class="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
        <p class="text-blue-100">Please wait while we process your transaction</p>
      </div>
    </div>

    <mat-card-content class="p-6 mt-2 min-h-[384px]">
      <div class="flex justify-center">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
    </mat-card-content>
  </mat-card>
  }
</div>